import { useCallback, useEffect, useRef } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  listPointState,
  selectedPoint,
  selectedSubImageState
} from 'store/buildingStore'
import { KrpanoInitProps, KrpanoInterface } from 'types/krpano'
import Krpano from 'utils/krpano'

export default function usePreviewKrpano() {
  const currentSelectedPoint = useRecoilValue(selectedPoint)
  const [points] = useRecoilState(listPointState)
  const [selectedSubImage] = useRecoilState(selectedSubImageState)

  // points の変化で embedpano の参照を変えたくないため ref 経由で保持。
  // ポイント追加など points 配列の更新で不要な krpano 再描画が発生するのを防ぐ。
  const pointsRef = useRef(points)
  useEffect(() => {
    pointsRef.current = points
  }, [points])

  const krpanoOnreadyCallback = useCallback(
    async (krpano: KrpanoInterface, krpanoInitProps: KrpanoInitProps) => {
      // get initial variables when embed pano
      const { h, v, z, previewUrl, tilesUrl, multires, imageUrl } =
        krpanoInitProps

      const varString = `layer[set_first_view_button].visible=true&view.hlookat=${h}&view.vlookat=${v}&view.fov=${z}`

      await krpano.call(
        `loadpano('/krpano/xml-assets/pano_preview.xml','${varString}',IGNOREKEEP,BLEND(1));`
      )
      const sceneName = Krpano.getScenceNameByFileId(
        currentSelectedPoint,
        pointsRef.current,
        selectedSubImage
      )
      const screenString = Krpano.generateSceneXML({
        sceneName,
        tilesUrl: tilesUrl || undefined,
        multires: multires || undefined,
        s3Path: imageUrl,
        thumbnailUrl: previewUrl || ''
      })

      krpano.call(`loadxml(${screenString}, null, MERGE);`)

      krpano.call(`load_scene(${sceneName},${h},${v},${z})`)
    },
    [currentSelectedPoint, points, selectedSubImage]
  )

  const embedpano = useCallback(
    (krpanoInitProps: KrpanoInitProps) => {
      const krpano = document.getElementById(
        'embedpano-preview'
      ) as unknown as KrpanoInterface
      const target = document.getElementById('preview-krpano-container')
      const { imageUrl, previewUrl, z, h, v, multires, tilesUrl } =
        krpanoInitProps

      if (target) {
        if (!krpano) {
          ;(window as any)?.embedpano({
            id: 'embedpano-preview',
            xml: null,
            target: 'preview-krpano-container',
            html5: 'auto+webgl',
            mwheel: false,
            bgcolor: '#FFFFFF',
            onready: (k: KrpanoInterface) => {
              krpanoOnreadyCallback(k, krpanoInitProps)
            }
          })
        } else {
          const sceneName = Krpano.getScenceNameByFileId(
            currentSelectedPoint,
            pointsRef.current,
            selectedSubImage
          )
          // 現在のシーンと同じシーンを再ロードしようとしている場合は何もしない。
          // points 更新（reorder 後の setPoints 連鎖）など、シーン変更を伴わない
          // 経路で embedPreview が呼ばれた際に load_scene で view が初期値にリセット
          // されるのを防ぐため。
          const currentSceneName = krpano.get('xml.scene')
          if (currentSceneName === sceneName) {
            return
          }
          if (krpano.get(`scene[${sceneName}]`) === null) {
            const screenString = Krpano.generateSceneXML({
              sceneName,
              tilesUrl: tilesUrl || undefined,
              multires: multires || undefined,
              s3Path: imageUrl,
              thumbnailUrl: previewUrl || ''
            })
            krpano.call(`loadxml(${screenString}, null, MERGE);`)
          }
          krpano.call(`load_scene(${sceneName},${h},${v},${z})`)
        }
      }
    },
    [currentSelectedPoint, points, selectedSubImage, krpanoOnreadyCallback]
  )

  useEffect(() => {
    return () => {
      Krpano.UnmountPanoPreview()
    }
  }, [])

  return {
    embedpano,
    currentSelectedPoint
  }
}
